"use strict";

import filterGroups from "vwa/utils/filterGroups";

import dispatcher from "./../../dispatcher";
import poiStore from "./../../store/pois";
import pinnedPOIStore from "./../../store/pinnedPOIs";
import selectedPOIStore from "./../../store/selectedPOI";
import editedPOIStore from "./../../store/editedPOI";
import poiModificationStore from "./../../store/poiModifications";
import savePOI from "./../../api/savePOI";
import poiUtils from "./../../utils/poi";
import {latLngToPoint, pointToLatLng} from "./../../utils/geo";

import lang from "vwa/lang";

import zIndex from "./zIndex";

import Toast from "vwa/components/Toast";
import ConfirmDialog from "vwa/components/ConfirmDialog";

const DEFAULT_COLOR = "red";
const DEFAULT_STROKE_OPACITY = 0.1;
const FILL_OPACITY = 0.3;
const STROKE_WEIGHT = 2;
const HIGHLIGHTED_STROKE_OPACITY = 1;

export default class POIController {
	
	constructor(map) {
		this.map = map;
		this.initPOIs();
		this.setListeners();
	}
	
	initPOIs() {
		this.pois = this.pois || {};
		this.removeAllPOIs();
		this.addPOIs(pinnedPOIStore.getAll());
	}
	
	setListeners() {
		google.maps.event.addListener(this.map, 'click', () => this.handleMapClick());
		google.maps.event.addListener(this.map, 'bounds_changed', () => this.updateVisibilities());
		
		pinnedPOIStore.on('set', _ => this.initPOIs());
		pinnedPOIStore.on('insert', poiIds => this.addPOIs(poiIds));
		pinnedPOIStore.on('remove', poiIds => this.removePOIs(poiIds));
		selectedPOIStore.on('change', poiId => this.updateSelectedAndEditedPOI());
		editedPOIStore.on('change', editing => this.updateSelectedAndEditedPOI());
		poiModificationStore.on('update', modificationDoc => this.updateShape(modificationDoc));
		poiModificationStore.on('insert', modificationDocs => this.updateShape(modificationDocs[0] || null));
		poiModificationStore.on('remove', modificationDocs => this.updateShape(modificationDocs[0] || null));
	}
	
	addPOIs(poiIds) {
		if (!poiIds)
			return;
		
		poiIds.forEach(poiId => this.addPOI(poiId));
	}
	
	addPOI(poiId) {
		if (poiId in this.pois)
			return;
		const type = poiUtils.getTypeOfPOI(poiId);
		if (type == "PolygonalPOI") this.addPolygonPOI(poiId);
		else if (type == "RadialPOI") this.addRadialPOI(poiId);
		else return;
		
		this.setPOIListeners(poiId);
	}
	
	addPolygonPOI(poiId) {
		const paths = poiUtils.getPathOfPOI(poiId);
		if (!paths) return;
		
		this.pois[poiId] = new google.maps.Polygon({
			map:			this.map,
			paths:			paths,
			strokeColor:	poiUtils.getColorOfPOI(poiId),
			strokeOpacity:	DEFAULT_STROKE_OPACITY,
			strokeWeight:	STROKE_WEIGHT,
			fillColor:		poiUtils.getColorOfPOI(poiId),
			fillOpacity:	FILL_OPACITY,
			editable:		false,
			draggable:		false,
			zIndex:			zIndex.POI
		});
	}
	
	addRadialPOI(poiId) {
		const center = poiUtils.getCenterOfPOI(poiId);
		const radius = poiUtils.getRadiusOfPOI(poiId);
		if (!center || radius === null)
			return;
		
		this.pois[poiId] = new google.maps.Circle({
			map:			this.map,
			strokeColor:	poiUtils.getColorOfPOI(poiId),
			strokeOpacity:	DEFAULT_STROKE_OPACITY,
			strokeWeight:	STROKE_WEIGHT,
			fillColor:		poiUtils.getColorOfPOI(poiId),
			fillOpacity:	FILL_OPACITY,
			editable:		false,
			draggable:		false,
			zIndex:			zIndex.POI,
			center:			center,
			radius:			radius
		});
	}
	
	setPOIListeners(poiId) {
		if (!this.pois[poiId])
			return;
		
		google.maps.event.addListener(this.pois[poiId], 'click', evt => this.handlePOIClick(evt, poiId));
		google.maps.event.addListener(this.pois[poiId], 'dblclick', evt => this.handlePOIDoubleClick(evt, poiId));
		google.maps.event.addListener(this.pois[poiId], 'mouseover', evt => this.handlePOIMouseOver(evt, poiId));
		google.maps.event.addListener(this.pois[poiId], 'mouseup', _ => this.handlePOIEditing(poiId));
		google.maps.event.addListener(this.pois[poiId], 'radius_changed', () => this.handleRadiusChange(poiId));
	}
	
	removePOIs(poiIds) {
		if (!poiIds)
			return;
		
		poiIds.forEach(poiId => {
			if (!this.pois[poiId])
				return;
			
			this.pois[poiId].setMap(null);
			delete this.pois[poiId];
		});
	}
	
	removeAllPOIs() {
		this.removePOIs(Object.keys(this.pois));
	}
	
	handleMapClick() {
		dispatcher.dispatch('selectPOI', null);
		this.closeEditing();
	}
	
	handlePOIClick(evt, poiId) {
		evt.stop();
		dispatcher.dispatch('selectPOI', poiId);
		if (editedPOIStore.get() !== poiId)
			this.closeEditing();
	}
	
	handlePOIDoubleClick(evt, poiId) {
		evt.stop();
		if (!this.pois[poiId])
			return;
		if (evt.vertex !== null && evt.vertex !== undefined) {
			this.pois[poiId].getPath().removeAt(evt.vertex);
			dispatcher.dispatch('modifyPOI', poiId, this.getModifications(poiId));
		} else {
			dispatcher.dispatch('editPOI', poiId);
		}
	}
	
	handleRadiusChange(poiId) {
		if (!this.pois[poiId])
			return;
		 
		 if (this.pois[poiId].getRadius() != poiUtils.getRadiusOfPOI(poiId))
			this.handlePOIEditing(poiId);
	}
	
	handlePOIEditing(poiId) {
		if (!editedPOIStore.get())
			return;
		setTimeout(() => 
			dispatcher.dispatch("modifyPOI", poiId, this.getModifications(poiId))
		);
	}
	
	handlePOIMouseOver(evt, poiId) {
		const tooltipId = Date.now();
		dispatcher.dispatch("createTooltip", tooltipId, evt.latLng, poiUtils.getNameOfPOI(poiId));
		google.maps.event.addListenerOnce(this.pois[poiId], 'mouseout', _ => dispatcher.dispatch("removeTooltip", tooltipId));
	}
	
	closeEditing() {
		const editedPOIId = editedPOIStore.get();
		if (!editedPOIId) return;
		const modificationDoc = poiModificationStore.get(editedPOIId);
		if (!modificationDoc || Object.keys(modificationDoc.modifications).length == 0) {
			dispatcher.dispatch('editPOI', null);
			return;
		} else {
			ConfirmDialog.open({
				text: lang.DO_YOU_WANT_TO_SAVE_CHANGES,
				cancelable: true,
				onDiscard: () => {
					dispatcher.dispatch("restorePOIShape", editedPOIId);
					dispatcher.dispatch("editPOI", null);
				},
				onConfirm: () => savePOI(editedPOIId)
			});
		}
	}
	
	getVisibilityOfPOI(poiId) {
		const poiBounds = poiUtils.getBoundsOfPOI(poiId);
		const mapBounds = this.map.getBounds();
		if (!poiBounds || !mapBounds)
			return;
		
		return mapBounds.intersects(poiBounds);
	}
	
	getLocationOfPOIShape(poiId) {
		if (!this.pois[poiId])
			return null;
		if (poiUtils.getTypeOfPOI(poiId) == "RadialPOI")
			return latLngToPoint(this.pois[poiId].getCenter());
		else if (poiUtils.getTypeOfPOI(poiId) == "PolygonalPOI")
			return mvcArrayToMultiPolygon(this.pois[poiId].getPath());
		return null;
	}
	
	getModifications(poiId) {
		
		if (!this.pois[poiId])
			return null;
		
		if (poiUtils.getTypeOfPOI(poiId) == "RadialPOI") {
			return {
				location:	this.getLocationOfPOIShape(poiId),
				radius:		this.pois[poiId].getRadius()
			};
		} else if (poiUtils.getTypeOfPOI(poiId) == "PolygonalPOI") {
			return {
				location:	this.getLocationOfPOIShape(poiId)
			};
		} else return null;
	}
	
	updateVisibilities() {
		for (let poiId in this.pois) {
			this.pois[poiId].setVisible(this.getVisibilityOfPOI(poiId));
		}
	}
	
	updateFocus(poiId) {
		const bounds =  poiUtils.getBoundsOfPOI(poiId);
		if (!bounds) return;
		
		this.map.fitBounds(bounds);
	}
	
	updateSelectedAndEditedPOI() {
		const editedPOIId = editedPOIStore.get();
		const selectedPOIId = selectedPOIStore.get();
		for (let poiId in this.pois) {
			const selected = poiId == selectedPOIId;
			const editable = poiId == editedPOIId;
			const draggable = editable;
			const strokeOpacity = editable || selected ? HIGHLIGHTED_STROKE_OPACITY : DEFAULT_STROKE_OPACITY;
			this.pois[poiId].setOptions({strokeOpacity, editable, draggable});
		}
		this.updateZIndexes();
	}
	
	updateZIndexes() {
		for (let poiId in this.pois) {
			this.pois[poiId].setOptions({zIndex: zIndex.POI});
		}
	}
	
	updateShape(modificationDoc) {
		if (!modificationDoc || !this.pois[modificationDoc.poi])
			return;
		const type = poiUtils.getTypeOfPOI(modificationDoc.poi);
		if (type == "RadialPOI") {
			this.pois[modificationDoc.poi].setCenter(poiUtils.getCenterOfPOI(modificationDoc.poi));
			this.pois[modificationDoc.poi].setRadius(poiUtils.getRadiusOfPOI(modificationDoc.poi));
		} else if (type == "PolygonalPOI") {
			this.pois[modificationDoc.poi].setPath(poiUtils.getPathOfPOI(modificationDoc.poi));
		}
		const color = poiUtils.getColorOfPOI(modificationDoc.poi);
		this.pois[modificationDoc.poi].setOptions({fillColor: color, strokeColor: color});
	}
}

function mvcArrayToMultiPolygon(mvcArray) {
	const first = mvcArray.getAt(0);
	return {
		type: "MultiPolygon",
		coordinates: [[[...mvcArray.getArray().map(latLng => [latLng.lng(), latLng.lat()]), [first.lng(), first.lat()]]]]
	};
}


// WEBPACK FOOTER //
// ./src/view/Map/POIController.js